import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";
import { logout } from "../features/userSlice";
import { getEmbeddedToken } from "../../utils/shoplineEmbedded";
import { isAuthExpiredResponse } from "../../utils/authRefresh";

const baseUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/`;

// Create a new mutex
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl,
  // SHOPLINE embedded (iframe): the login cookie is blocked, so send the session token
  // directly on every API call (doesn't rely on the global fetch patch, which other
  // scripts on the page can bypass by wrapping fetch themselves).
  prepareHeaders: (headers) => {
    const token = getEmbeddedToken();
    if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  },
});

const customFetchBase: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);
  // The access token is missing / expired: refresh it and retry. (FastAPI sends the text as
  // `detail`, older responses used `message`; an expired token is "Token is invalid or has expired".)
  const errorStatus = result.error?.status;
  const errorData: any = result.error?.data;
  if (
    errorData?.message === "You are not logged in" ||
    isAuthExpiredResponse(typeof errorStatus === "number" ? errorStatus : undefined, errorData)
  ) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const refreshResult = await baseQuery(
          { credentials: "include", url: "auth/refresh" },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          // Retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logout());
          localStorage.removeItem("payment");  
          localStorage.removeItem("msg");
          localStorage.removeItem("complexity");
          localStorage.removeItem("module");
          localStorage.removeItem("subscription_level");
          localStorage.removeItem("free_plan");
          localStorage.removeItem("user_email");
          localStorage.setItem("logged_in", "false");
          localStorage.clear();
          window.location.href = "/signin";
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export default customFetchBase;
